/* eslint-disable array-callback-return */
/* eslint-disable react/destructuring-assignment */
import { css } from '@emotion/react';
import { Col, Collapse, Icon, Input, notification, Select } from 'antd';
import moment from 'moment';
import Router, { withRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  requestCharts,
  requestSearchOptions,
  resetResults,
  resetSearchFilters,
  saveCompliassistSearch,
  setCurrentPage,
  setSearchandFetchResults,
  setSearchFilters,
  setSelectedSearch,
  updateSearchFilters,
} from '@/src/compliance/redux/complianceReducer';
import { isSearchFiltersEmpty } from '@/src/compliance/utils';
import parseURL from '@/src/compliance/utils/parseURL';
import DateRangePicker from '@/src/shared/components/DateRangePicker';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import { form as formStyle } from '@/styles';
import style from '@/styles/utilities/searchFilters';
import { convertMomentToDate, disableDayAfter, validateCustomRange } from '@/utils/dateTimeHelpers';
import formatURL from '@/utils/formatURL';
import { isTruthySingleOrArray } from '@/utils/formHelpers';
import isObjectEmpty from '@/utils/isObjectEmpty';
import { escapeGTMParam, trackGTMEvent } from '@/utils/utils';

import Buttons from './Buttons';

const filterColorOverride = css`
  background-color: #fff
`;

const initialSearch = {
  searchId: null,
  personId: null,
  fullName: null,
  searchDescription: null,
  includeOnHome: null,
  searchText: null,
  states: [],
  categories: [],
  charts: [],
  dateStart: null,
  dateEnd: null,
  page: null,
  pageSize: null,
  sortField: null,
  sortOrder: null,
};

const FIELD_NAMES = ['searchText', 'states', 'categories', 'charts', 'dateRanges'];

const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

const { Panel } = Collapse;

const normalizeCategory = category => ({
  categoryId: category.categoryId,
  category: category.category || category.categoryDescription,
});

const normalizeChart = chart => ({
  categories: (chart.categories || []).map(normalizeCategory),
  chartId: chart.chartId,
  chartTitle: chart.chartTitle,
  chartDescription: chart.chartDescription,
});

const buildRequestChartsPayload = ({
  values = {},
  categories = [],
  charts = [],
  states = [],
  personId = 0,
  isRecentChanges = false,
}) => {
  const selectedCategory = values.categories
    ? categories.find(c => Number(c.categoryId) === Number(values.categories))
    : null;

  const selectedCharts = Array.isArray(values.charts)
    ? charts.filter(c => values.charts.map(Number).includes(Number(c.chartId)))
    : [];

  const selectedStates = Array.isArray(values.states)
    ? states.filter(s => values.states.includes(s.stateCode))
    : [];

  return {
    searchId: null,
    personId: personId || 0,
    fullName: null,
    searchDescription: null,
    includeOnHome: null,
    searchText: values.searchText || null,
    states: selectedStates,
    categories: selectedCategory ? [normalizeCategory(selectedCategory)] : [],
    charts: selectedCharts.map(normalizeChart),
    dateStart: Array.isArray(values.dateRanges)
      ? convertMomentToDate(values.dateRanges[0])
      : values.dateStart || null,
    dateEnd: Array.isArray(values.dateRanges)
      ? convertMomentToDate(values.dateRanges[1])
      : values.dateEnd || null,
    page: null,
    pageSize: null,
    sortField: null,
    sortOrder: null,
    ...(isRecentChanges ? { recentChanges: 'Y' } : {}),
  };
};

const buildRequestChartsPayloadFromUrl = ({ payload = {}, personId = 0, isRecentChanges = false }) => ({
  searchId: null,
  personId: personId || 0,
  fullName: null,
  searchDescription: null,
  includeOnHome: null,
  searchText: payload.searchText || null,
  states: payload.states || [],
  categories: Array.isArray(payload.categories)
    ? payload.categories.map(normalizeCategory)
    : [],
  charts: Array.isArray(payload.charts)
    ? payload.charts.map(normalizeChart)
    : [],
  dateStart: payload.dateStart || null,
  dateEnd: payload.dateEnd || null,
  page: null,
  pageSize: null,
  sortField: null,
  sortOrder: null,
  ...(isRecentChanges ? { recentChanges: 'Y' } : {}),
});

const SearchFilters = props => {
  const {
    form,
    filterExpanded,
    setSelection,
    setSelectionTags,
    searchFilterKey,
    handleFiltersCollapseChange,
    router,
    resetTags,
    includeDates,
    rootSearchURL,
  } = props;

  //identify if recent changes
  const isRecentChanges = rootSearchURL.includes('recent-changes');

  const dispatch = useDispatch();

  const { searchOptions, searchFiltersValues } = useSelector(state => state.compliance);

  const {
    isLoadingOptions,
    categories = [],
    charts = [],
    states = [],
  } = searchOptions;

  const personId = useSelector(state => state.auth.userinfo.PersonID);

  const { getFieldDecorator, validateFieldsAndScroll } = form;

  const searchOptionsPayload = {
    ...initialSearch,
    categories,
    charts,
    states,
  };

  const selectedCategoryValue = form.getFieldValue('categories');

  const filteredCharts = selectedCategoryValue
    ? charts.filter(chart => {
      return (chart.categories || [])
        .map(cat => Number(cat.categoryId))
        .includes(Number(selectedCategoryValue));
    })
    : charts;

  const { query, asPath } = router;
  const queryString = JSON.stringify(query);
  const searchOptionsString = JSON.stringify(searchOptionsPayload);

  const searchFiltersValuesString = JSON.stringify(searchFiltersValues);

  useEffect(() => {
    dispatch(requestSearchOptions());
  }, []);

  useEffect(() => {
    const currentDateRanges = form.getFieldValue('dateRanges') || [null, null];

    form.setFieldsValue({
      searchText: searchFiltersValues.searchText || '',
      states: searchFiltersValues.states || [],
      categories: searchFiltersValues.categories || '',
      charts: searchFiltersValues.charts || [],
      dateRanges: [
        searchFiltersValues.dateStart
          ? moment(searchFiltersValues.dateStart)
          : currentDateRanges[0],
        searchFiltersValues.dateEnd
          ? moment(searchFiltersValues.dateEnd)
          : currentDateRanges[1],
      ],
    });
  }, [searchFiltersValuesString]);

  useEffect(() => {
    dispatch(setCurrentPage(1));

    if (isObjectEmpty(query)) {
      form.setFieldsValue({
        categories: '',
        charts: [],
        states: [],
        searchText: '',
        dateRanges: [null, null],
      });

      dispatch(resetResults());
      dispatch(resetSearchFilters());
      resetTags();

      return;
    }

    if (query.searchId) {
      if (!personId) return;

      const selectionTags = FIELD_NAMES.map(name => [null, null, name]);

      setSelectionTags(selectionTags);
      form.resetFields();

      dispatch(setSelectedSearch(query.searchId));
      dispatch(setSearchandFetchResults({ searchId: query.searchId, personId }));

      return;
    }

    const updatedPayload = parseURL(searchOptionsPayload, router);

    if (!isObjectEmpty(updatedPayload)) {
      const payload = {
        ...initialSearch,
        ...updatedPayload,
      };

      const selectionTags = [];

      if (updatedPayload.searchText) {
        const selectedNodes = {
          value: updatedPayload.searchText,
          input: 'searchText',
          children: updatedPayload.searchText,
        };
        const tag = [updatedPayload.searchText, selectedNodes, 'searchText', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.states && Array.isArray(updatedPayload.states)) {
        const selectedStates = updatedPayload.states.map(s => s.stateCode);
        const selectedNodes = updatedPayload.states.map(s => {
          return {
            value: s.stateCode,
            children: s.stateDescription,
            input: 'states',
          };
        });
        const tag = [selectedStates, selectedNodes, 'states', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.categories && Array.isArray(updatedPayload.categories)) {
        if (!updatedPayload.categories.length) return;

        const selectedNodes = {
          value: updatedPayload.categories[0].categoryId,
          input: 'categories',
          children: updatedPayload.categories[0].categoryDescription,
        };
        const tag = [undefined, selectedNodes, 'categories', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.charts && Array.isArray(updatedPayload.charts)) {
        const selectedCharts = updatedPayload.charts.map(s => s.chartId);
        const selectedNodes = updatedPayload.charts.map(s => {
          return {
            value: s.chartId,
            children: s.chartTitle,
            input: 'charts',
          };
        });
        const tag = [selectedCharts, selectedNodes, 'charts', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.dateStart && updatedPayload.dateEnd) {
        const dateRanges = [moment(updatedPayload.dateStart), moment(updatedPayload.dateEnd)];
        const selectedNodes = {
          value: dateRanges,
          children: `${updatedPayload.dateStart}-${updatedPayload.dateEnd}`,
          input: 'dateRanges',
        };
        const tag = [undefined, selectedNodes, 'dateRanges', true];
        selectionTags.push(tag);
      }

      const formattedPayload = {
        searchText: payload.searchText,
        categories:
          Array.isArray(payload.categories) && payload.categories.length
            ? payload.categories.map(c => c.categoryId)[0]
            : undefined,
        charts:
          Array.isArray(payload.charts) && payload.charts.length
            ? payload.charts.map(c => c.chartId)
            : [],
        states:
          Array.isArray(payload.states) && payload.states.length
            ? payload.states.map(s => s.stateCode)
            : [],
        dateStart: payload.dateStart,
        dateEnd: payload.dateEnd,
      };

      const requestChartsPayload = buildRequestChartsPayloadFromUrl({
        payload,
        personId,
        isRecentChanges,
      });

      setSelectionTags(selectionTags);
      dispatch(setSearchFilters(formattedPayload));
      dispatch(requestCharts({ payload: requestChartsPayload }));
    }
  }, [queryString, searchOptionsString, asPath, personId]);

  const handleCategoryChange = categoryId => {
    const chartValue = form.getFieldValue('charts');

    if (!Array.isArray(chartValue)) return;

    const hasInvalidChart = chartValue.some(c => {
      const chart = charts.find(ch => Number(ch.chartId) === Number(c));

      return !chart || !(chart.categories || [])
        .map(cat => Number(cat.categoryId))
        .includes(Number(categoryId));
    });

    if (hasInvalidChart) {
      form.setFieldsValue({ charts: [] });
      updateSearch('charts', []);
    }
  };

  const handleReset = () => {
    form.setFieldsValue({
      categories: '',
      charts: [],
      states: [],
      searchText: '',
      dateRanges: [null, null],
    });

    dispatch(resetResults());
    dispatch(resetSearchFilters());
    resetTags();

    const formattedURL = rootSearchURL;
    Router.push(formattedURL, formattedURL, { shallow: true });
  };

  const updateSearch = (field, value) => {
    dispatch(updateSearchFilters({ field, value }));
  };

  const renderSelect = ({
    initialValue,
    placeholder,
    options,
    optId,
    optDescription,
    fieldName,
    header,
    extra = '',
    extraProps = {},
  }) => {
    const isActive = isTruthySingleOrArray(initialValue);
    const activeProp = isActive ? { defaultActiveKey: '1' } : {};

    return (
      <Collapse {...filterCollapseProps} {...activeProp} css={filterColorOverride}>
        <Panel
          key="1"
          header={
            <Heading type={4} weight="bolder" theme="primaryGray">
              {header}
            </Heading>
          }
        >
          <Form.Item extra={extra}>
            {getFieldDecorator(fieldName, {
              initialValue,
            })(
              <Select
                css={formStyle.inputGray}
                size="large"
                placeholder={placeholder}
                optionFilterProp="children"
                showSearch
                allowClear
                onChange={(v, s) => {
                  setSelection(v, s, fieldName);
                  updateSearch(fieldName, v);
                }}
                {...extraProps}
              >
                {(options || []).map(opt => (
                  <Select.Option key={`${opt[optId]}`} value={opt[optId]}>
                    {opt[optDescription]}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Panel>
      </Collapse>
    );
  };

  const onSearch = async () => {
    await validateFieldsAndScroll(async (err, values) => {
      if (err) return;

      const dateStart =
        Array.isArray(values.dateRanges) && values.dateRanges[0]
          ? convertMomentToDate(values.dateRanges[0])
          : null;

      const dateEnd =
        Array.isArray(values.dateRanges) && values.dateRanges[1]
          ? convertMomentToDate(values.dateRanges[1])
          : null;

      const queryObj = {
        searchtext: values.searchText,
        categories: values.categories,
        charts: (values.charts || []).join(','),
        states: (values.states || []).join(','),
        datestart: dateStart,
        dateend: dateEnd,
      };

      const formattedPayload = {
        searchText: values.searchText || '',
        categories: values.categories || '',
        charts: values.charts || [],
        states: values.states || [],
        dateStart,
        dateEnd,
      };

      const payload = buildRequestChartsPayload({
        values,
        categories,
        charts,
        states,
        personId,
        isRecentChanges,
      });

      trackGTMEvent({
        event: 'complianceSearch',
        text: escapeGTMParam(values.searchText || ''),
        state: values.states?.length > 0 ? values.states.join(', ') : 'All',
        category: values.categories
          ? categories
            .filter(c => c.categoryId === values.categories)
            .map(c => escapeGTMParam(c.categoryDescription))
            .join(', ')
          : 'All',
        chart:
          values.charts?.length > 0
            ? charts
              .filter(c => (values.charts || []).includes(c.chartId))
              .map(c => escapeGTMParam(c.chartTitle))
              .join(', ')
            : 'All',
      });

      dispatch(setCurrentPage(1));
      dispatch(setSearchFilters(formattedPayload));

      const formattedURL = formatURL(rootSearchURL, queryObj);
      Router.push(formattedURL, formattedURL, { shallow: true });

      dispatch(requestCharts({ payload }));
    });
  };
  const handleSubmit = e => {
    e.preventDefault();

    if (isSearchFiltersEmpty(form)) {
      notification.error({
        message: 'Search',
        description: 'Search option is required.',
      });
      return;
    }

    onSearch();
  };

  const handleFiltersCollapse = keys => {
    const isOpen = keys?.length > 0;

    trackGTMEvent({
      event: isOpen ? 'expandSearch' : 'collapseSearch',
    });

    handleFiltersCollapseChange(keys || []);
  };

  return (
    <Col xs={24} md={filterExpanded ? 24 : 12} lg={filterExpanded ? 24 : 8}>
      <Form onSubmit={handleSubmit}>
        <div css={style.filters} key={searchFilterKey}>
          <Collapse
            defaultActiveKey={['1']}
            {...filterCollapseProps}
            onChange={handleFiltersCollapse}
            css={filterColorOverride}
          >
            <Panel
              isActive
              key="1"
              defaultActiveKey={['1']}
              header={
                <Heading type={3} weight="bolder" theme="primaryGray">
                  Search Filters
                </Heading>
              }
              id={filterExpanded ? 'expandpanel' : 'collapsepanel'}
            >
              <Form.Item>
                <div className={style['ca-filter__search']} style={{ display: 'flex' }}>
                  {getFieldDecorator('searchText', {
                    initialValue: searchFiltersValues.searchText,
                  })(
                    <Input
                      prefix={<Icon type="search" />}
                      placeholder="Search"
                      css={formStyle.inputGray}
                      onChange={e => {
                        setSelection(e.target.value, null, 'searchText');
                        updateSearch('searchText', e.target.value);
                      }}
                    />
                  )}
                </div>
              </Form.Item>

              {renderSelect({
                placeholder: 'Select State',
                initialValue: searchFiltersValues.states,
                options: states,
                optId: 'stateCode',
                optDescription: 'stateDescription',
                fieldName: 'states',
                header: 'State',
                extra: 'Select one or more states.',
                extraProps: {
                  mode: 'multiple',
                },
              })}

              {renderSelect({
                placeholder: 'Select Category',
                initialValue: searchFiltersValues.categories,
                options: categories,
                optId: 'categoryId',
                optDescription: 'categoryDescription',
                fieldName: 'categories',
                header: 'Category',
                extra: 'Select a category to see a list of available charts.',
                extraProps: {
                  onChange: (val, s) => {
                    handleCategoryChange(val);
                    setSelection(val, s, 'categories');
                    updateSearch('categories', val);
                  },
                },
              })}

              {renderSelect({
                placeholder: 'Select Chart',
                initialValue: searchFiltersValues.charts,
                options: filteredCharts,
                optId: 'chartId',
                optDescription: 'chartTitle',
                fieldName: 'charts',
                header: 'Chart',
                extra: 'Select one or more charts.',
                extraProps: {
                  mode: 'multiple',
                },
              })}

              {includeDates && (
                <Collapse {...filterCollapseProps} css={filterColorOverride}>
                  <Panel
                    key="1"
                    header={
                      <Heading type={4} weight="bolder" theme="primaryGray">
                        Date Range
                      </Heading>
                    }
                  >
                    <Form.Item>
                      {form.getFieldDecorator('dateRanges', {
                        initialValue:
                          searchFiltersValues.dateStart && searchFiltersValues.dateEnd
                            ? [
                              moment(searchFiltersValues.dateStart),
                              moment(searchFiltersValues.dateEnd),
                            ]
                            : [null, null],
                        rules: [
                          {
                            message: 'Please complete start date and end date',
                            validator: validateCustomRange,
                          },
                        ],
                      })(
                        <DateRangePicker
                          size="large"
                          css={formStyle.inputGray}
                          onChange={(value, s) => {
                            setSelection(value, s, 'dateRanges');
                            updateSearch(
                              'dateStart',
                              Array.isArray(value) && convertMomentToDate(value[0])
                            );
                            updateSearch(
                              'dateEnd',
                              Array.isArray(value) && convertMomentToDate(value[1])
                            );
                          }}
                          disabledDate={disableDayAfter}
                        />
                      )}
                    </Form.Item>
                  </Panel>
                </Collapse>
              )}
            </Panel>
          </Collapse>
          <Buttons
            form={form}
            searchOptions={searchOptionsPayload}
            btnDisabled={isLoadingOptions}
            handleSaveSearch={saveCompliassistSearch}
            handleClear={handleReset}
            isRecentChanges={isRecentChanges}
          />
        </div>
      </Form>
    </Col>
  );
};

SearchFilters.propTypes = {};

export default withRouter(SearchFilters);